import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, TrendingUp, ArrowDown } from 'lucide-react';
import { SECTION_ORDER, SECTION_LABELS } from '../peptide/peptideContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-lg border border-gray-100 p-4" data-testid={testid}>
    <p className="text-2xl font-bold text-gray-900">{value ?? 0}</p>
    <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
  </div>
);

const ScrollDepthViz = ({ scroll }) => {
  const totalExits = SECTION_ORDER.reduce((s, k) => s + (scroll[k] || 0), 0);
  if (totalExits === 0) {
    return <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-4">No scroll-depth data yet — it records the deepest section each visitor reaches before leaving.</p>;
  }
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-1.5" data-testid="funnel-scroll-depth">
      {SECTION_ORDER.map((k, i) => {
        const reached = SECTION_ORDER.slice(i).reduce((s, kk) => s + (scroll[kk] || 0), 0);
        const pct = Math.round((reached / totalExits) * 100);
        const drop = scroll[k] || 0;
        return (
          <div key={k} className="flex items-center gap-3" data-testid={`scroll-row-${k}`}>
            <span className="w-40 text-[11px] text-gray-500 truncate">{SECTION_LABELS[k] || k}</span>
            <div className="flex-1 h-4 bg-gray-50 rounded overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-400 to-cyan-500 rounded" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-16 text-[11px] font-semibold text-gray-700 text-right">{pct}%</span>
            <span className={`w-24 text-[11px] text-right ${drop > 0 ? 'text-rose-500 font-medium' : 'text-gray-400'}`}>
              {drop > 0 ? `${drop} left here` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default function PeptideFunnelTab() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/peptide-funnel`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (e) { setErr(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!data) return <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />;

  const ev = data.page_events || {};
  const bhn = data.bhn_report || {};
  const conversions = bhn.conversions || [];

  return (
    <div className="space-y-8" data-testid="peptide-funnel-tab">
      <div>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" /> Peptide Subscription Funnel</h2>
        <p className="text-sm text-gray-500">Non-clinical reporting only — page traffic, CTA clicks, attributed conversions, gifts, and points. No health data is ever displayed here.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Page views" value={ev.NA_subscription_page_view} testid="funnel-page-views" />
        <Stat label="CTA clicks" value={ev.NA_membership_cta_click} testid="funnel-cta-clicks" />
        <Stat label="Referrals minted" value={data.referrals?.minted} testid="funnel-referrals-minted" />
        <Stat label="Paid conversions" value={conversions.length} testid="funnel-conversions" />
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">CTA clicks by placement</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.cta_by_placement || {}).map(([p, n]) => (
            <span key={p} className="px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded text-xs text-gray-600">{p}: <strong>{n}</strong></span>
          ))}
          {Object.keys(data.cta_by_placement || {}).length === 0 && <p className="text-xs text-gray-400">No CTA clicks tracked yet.</p>}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5"><ArrowDown className="w-4 h-4 text-rose-500" /> Scroll depth — where visitors leave</h3>
        <p className="text-xs text-gray-400 mb-2">% of visitors reaching each section (deepest section per visit, no PII).</p>
        <ScrollDepthViz scroll={data.scroll_depth || {}} />
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">Attributed conversions (from Biohacker Network — aggregate only)</h3>
        {conversions.length === 0 ? (
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-4">No paid membership conversions attributed yet. Only verified Stripe payments count as conversions.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm" data-testid="funnel-conversions-table">
              <thead>
                <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-400">
                  <th className="px-4 py-3">Referral</th><th className="px-4 py-3">Placement</th>
                  <th className="px-4 py-3">Tier</th><th className="px-4 py-3">Converted</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3">Gift</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((c) => (
                  <tr key={c.referral_id} className="border-b border-gray-50">
                    <td className="px-4 py-2.5 text-gray-500 text-xs font-mono">{(c.referral_id || '').slice(0, 8)}…</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{c.placement}</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900 capitalize">{c.membership_tier}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{(c.converted_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-xs"><span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 capitalize">{c.status}</span></td>
                    <td className="px-4 py-2.5 text-xs"><span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 capitalize">{c.gift_status || 'none'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">Gift entitlements</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(bhn.gift_statuses || {}).map(([s, n]) => (
              <span key={s} className="px-2.5 py-1.5 bg-amber-50 border border-amber-100 rounded text-xs text-amber-700">{s}: <strong>{n}</strong></span>
            ))}
            {Object.keys(bhn.gift_statuses || {}).length === 0 && <p className="text-xs text-gray-400">None yet.</p>}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">Points redemptions</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(bhn.points_redemptions || {}).map(([s, n]) => (
              <span key={s} className="px-2.5 py-1.5 bg-emerald-50 border border-emerald-100 rounded text-xs text-emerald-700">{s}: <strong>{n}</strong></span>
            ))}
            {Object.keys(bhn.points_redemptions || {}).length === 0 && <p className="text-xs text-gray-400">None yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
